// src/components/shipment/FilterBar.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import MoreFiltersDrawer from "./MoreFiltersDrawer";
import SortDropdown from "./SortDropdown";

const FilterBar = ({
  onFilter = () => {},
  stats = { total: 0 },
  uniqueCarriers = {},
}) => {
  // 🔥 Local States
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [polFilter, setPolFilter] = useState("");
  const [podFilter, setPodFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [sortBy, setSortBy] = useState("recent");

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Applied filters (only applied on button click)
  const [appliedFilters, setAppliedFilters] = useState({
    carrier: "all",
    pol: "",
    pod: "",
    dateFrom: "",
    dateTo: "",
  });

  // 🔥 Active filter count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (carrierFilter !== "all") count++;
    if (polFilter) count++;
    if (podFilter) count++;
    if (dateRange.from) count++;
    if (dateRange.to) count++;
    return count;
  }, [carrierFilter, polFilter, podFilter, dateRange]);

  // 🔥 Central Filter Trigger

  const triggerFilter = (filter) => {
    const payload = {
      search: searchText,
      status: statusFilter,
      sortBy,
      ...filter,
    };

    onFilter(payload);
  };

  // 🔥 Debounced Search
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerFilter(appliedFilters);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // 🔥 Immediate trigger for status / sort / applied filters
  useEffect(() => {
    triggerFilter(appliedFilters);
  }, [statusFilter, sortBy]);

  // Clear all
  const clearAllFilters = () => {
    setCarrierFilter("all");
    setPolFilter("");
    setPodFilter("");
    setDateRange({ from: "", to: "" });
    const filter = {
      carrier: "all",
      pol: "",
      pod: "",
      dateFrom: "",
      dateTo: "",
    };
    setAppliedFilters(filter);
    triggerFilter(filter);
  };

  return (
    <div className="rounded-xl bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by Quote ID, Customer, POL, or POD..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>

        {/* Status Buttons */}
        <div className="flex items-center gap-2">
          {["all", "active", "intransit", "delayed"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {status === "all"
                ? "All"
                : status === "active"
                  ? "Active"
                  : status === "intransit"
                    ? "In Transit"
                    : "Delayed"}
            </button>
          ))}
        </div>

        {/* More Filters */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMoreFilters(!showMoreFilters);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showMoreFilters || activeFiltersCount > 0
                ? "bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30"
                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            More Filters
            {activeFiltersCount > 0 && (
              <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <MoreFiltersDrawer
            isOpen={showMoreFilters}
            onClose={() => setShowMoreFilters(false)}
            carrierFilter={carrierFilter}
            setCarrierFilter={setCarrierFilter}
            polFilter={polFilter}
            setPolFilter={setPolFilter}
            podFilter={podFilter}
            setPodFilter={setPodFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            activeFiltersCount={activeFiltersCount}
            uniqueCarriers={uniqueCarriers}
            clearAllFilters={clearAllFilters}
            onApply={() => {
              const filter = {
                carrier: carrierFilter,
                pol: polFilter,
                pod: podFilter,
                dateFrom: dateRange.from,
                dateTo: dateRange.to,
              };
              setAppliedFilters(filter);
              triggerFilter(filter);
              setShowMoreFilters(false);
            }}
          />
        </div>

        {/* Sort */}
        <SortDropdown
          sortBy={sortBy}
          setSortBy={setSortBy}
          isOpen={showSortDropdown}
          setIsOpen={setShowSortDropdown}
        />
      </div>

      {/* Results */}
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
        Showing{" "}
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {stats?.showRecord}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {stats.total}
        </span>{" "}
        shipments
      </p>
    </div>
  );
};

export default FilterBar;
